import { useCallback, useContext, useEffect, useState } from "react";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import {
  QUERY_COMPANY_LIST,
  QUERY_PROJECT_INTEGRATION_USERS,
  QUERY_PROJECT_USERS,
  QUERY_PROJECT_VENDORS
} from "services/graphQL/queries";
import { TAuditChangeLog } from "change-events/change-event-polling";
import { useParams } from "react-router";
import { UserProjectRoles, EUserTypes } from "../constants";
import { useCIQQuery } from "./ciq-gql-hooks";

export type ProjectParticipants = {
  dataLoaded: boolean;
  gcReviewers: any;
  designReviewers: any;
  tradePartners: any;
  responsibleContractors: any;
  drCompanies: any;
  submitterUsers: any;
  materialTradePartners: any;
  allProjectUsersMap: any;
};

type TAuditChangeLogs = Array<TAuditChangeLog>;

// ============ CONSTANTS ============
const INITIAL_PARTICIPANTS_STATE: ProjectParticipants = {
  dataLoaded: false,
  gcReviewers: [],
  designReviewers: [],
  tradePartners: [],
  responsibleContractors: [],
  drCompanies: [],
  submitterUsers: [],
  materialTradePartners: [],
  allProjectUsersMap: null
};

// ============ SORTING HELPERS ============
const sortByFirstName = (arr: any[]): any[] =>
  [...arr].sort((a, b) => a.first_name.localeCompare(b.first_name));

const sortByVendorName = (arr: any[]): any[] =>
  [...arr].sort((a, b) =>
    a.subscription_vendor?.name?.localeCompare(b.subscription_vendor?.name)
  );

// ============ USER MAP HELPER ============
const createUserMap = (users: any[]): Record<string, any> => {
  const map: Record<string, any> = {};
  users.forEach((user) => {
    const id = user.id || user.user_id;
    map[id] = user;
  });
  return map;
};

// ============ POC USER EXTRACTION ============
/**
 * Extracts POC users for companies that have no actual users
 * @param vendorIdsWithActualUsers - Set of vendor IDs that already have actual users
 * @param includeEmail - DR POC users include email, submitter POC users don't
 */
const extractPOCUsers = (
  companies: any[],
  subscriptionCompanies: any[],
  vendorIdsWithActualUsers: Set<string>,
  includeEmail: boolean
): any[] => {
  const pocUsers: any[] = [];

  companies.forEach((company: any) => {
    // Skip companies that already have actual users
    if (vendorIdsWithActualUsers.has(company.vendor_id)) return;

    const foundCompanyFromSubscription = subscriptionCompanies.find(
      (comp: any) => comp.id === company.vendor_id
    );

    if (foundCompanyFromSubscription) {
      foundCompanyFromSubscription.trade_partner_pocs.forEach(
        (pocUser: any) => {
          const user: any = {
            id: `${pocUser.first_name} ${pocUser.last_name}`,
            first_name: pocUser.first_name,
            last_name: pocUser.last_name,
            company: {
              name: company?.subscription_vendor?.name,
              inviteStatus: company.project_vendors_company_status?.status,
              vendor_id: company?.vendor_id
            },
            type: "poc"
          };
          if (includeEmail) {
            user.email = pocUser.email || "";
          }
          pocUsers.push(user);
        }
      );
    }
  });

  return pocUsers;
};

/**
 * Extracts vendor IDs from a list of users that have company.vendor_id
 */
const getVendorIdsFromUsers = (users: any[]): Set<string> => {
  const vendorIds = new Set<string>();
  users.forEach((user) => {
    if (user.company?.vendor_id) {
      vendorIds.add(user.company.vendor_id);
    }
  });
  return vendorIds;
};

// ============ DATA PROCESSING FUNCTIONS ============
const extractGCReviewers = (projectUsers: any[]): any[] => {
  return projectUsers.map((item: any) => ({
    id: item.user_id,
    first_name: item.user.first_name,
    last_name: item.user.last_name,
    email: item.user.email || "",
    company: {
      name: item?.subscription_vendor?.name,
      inviteStatus: null
    },
    type: "actual",
    status_id: item?.status_id
  }));
};

const extractDesignReviewersActual = (projectUsers: any[]): any[] => {
  return projectUsers.map((item: any) => {
    const { user } = item;
    return {
      id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email || "",
      company: {
        vendor_id: item?.subscription_vendor?.id,
        name: item?.subscription_vendor?.name,
        inviteStatus:
          item.subscription_vendor?.project_vendors?.[0]
            ?.project_vendors_company_status?.status
      },
      type: "actual",
      status_id: item?.status_id
    };
  });
};

const extractTradePartners = (projectUsers: any[]): any[] => {
  return projectUsers.filter(
    (item: any) =>
      item.external ||
      UserProjectRoles[EUserTypes.SUBCONTRACTOR].includes(
        item?.project_role?.key
      )
  );
};

const extractSubmitterUsersActual = (
  projectCompanies: any[],
  projectUsers: any[]
): any[] => {
  const submitterUsers: any[] = [];

  projectCompanies.forEach((company: any) => {
    // if company status is Not Invited(1) then don't try to add its users
    // if (company.status === 1) return;

    const usersOfCompany = projectUsers.filter((user: any) => {
      const orgSubscriptions = user?.subscription?.organization_subscriptions;
      const orgName =
        (orgSubscriptions?.length && orgSubscriptions[0].organization?.name) ||
        "";
      const userSubVendor = user?.subscription_vendor;
      return (
        (userSubVendor && userSubVendor?.id === company?.vendor_id) ||
        (!userSubVendor && orgName === company?.subscription_vendor?.name)
      );
    });

    usersOfCompany.forEach((item: any) => {
      const { user } = item;
      submitterUsers.push({
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email || "",
        company: {
          name: company?.subscription_vendor?.name,
          inviteStatus: company?.project_vendors_company_status?.status,
          vendor_id: company?.vendor_id
        },
        type: "actual",
        status_id: item?.status_id
      });
    });
  });

  return submitterUsers;
};

// ============ MAIN PROCESSING FUNCTION ============
const processParticipantsData = (
  projectUsers: any[],
  projectCompanies: any[],
  subscriptionCompanies: any[]
): ProjectParticipants => {
  // Extract GC reviewers
  const gcReviewers = extractGCReviewers(projectUsers);

  // Extract design reviewers (actual users)
  const designReviewersActual = extractDesignReviewersActual(projectUsers);

  // Extract trade partners (raw items, not formatted)
  const tradePartners = extractTradePartners(projectUsers);

  // Material trade partners = all project companies
  const materialTradePartners = projectCompanies;

  // Filter responsible contractors (subcontractor type only)
  const responsibleContractors = projectCompanies.filter(
    (company: any) => company.type_id === EUserTypes.SUBCONTRACTOR
  );

  // Filter DR companies (architect and engineer types)
  const drCompanies = projectCompanies.filter(
    (company: any) =>
      company.type_id === EUserTypes.ARCHITECT ||
      company.type_id === EUserTypes.ENGINEER
  );

  // Extract submitter users (actual + POC)
  const submitterUsersActual = extractSubmitterUsersActual(
    materialTradePartners,
    projectUsers
  );
  // Get vendor IDs that have actual submitter users
  const submitterVendorIdsWithActualUsers =
    getVendorIdsFromUsers(submitterUsersActual);
  const submitterPOCUsers = extractPOCUsers(
    materialTradePartners,
    subscriptionCompanies,
    submitterVendorIdsWithActualUsers,
    false // submitter POC users don't include email
  );
  const submitterUsers = [...submitterUsersActual, ...submitterPOCUsers];

  // Get vendor IDs that have actual design reviewer users
  const drVendorIdsWithActualUsers = getVendorIdsFromUsers(
    designReviewersActual
  );
  // Extract DR POC users and combine with design reviewers
  const drPOCUsers = extractPOCUsers(
    drCompanies,
    subscriptionCompanies,
    drVendorIdsWithActualUsers,
    true // DR POC users include email
  );
  const designReviewers = [...designReviewersActual, ...drPOCUsers];

  // Sort all collections
  const sortedGcReviewers = sortByFirstName(gcReviewers);
  const sortedDesignReviewers = sortByFirstName(designReviewers);
  const sortedSubmitterUsers = sortByFirstName(submitterUsers);
  const sortedResponsibleContractors = sortByVendorName(responsibleContractors);
  const sortedMaterialTradePartners = sortByVendorName(materialTradePartners);
  const sortedDrCompanies = sortByVendorName(drCompanies);

  // Build user map from sorted arrays
  const allProjectUsers = [
    ...sortedGcReviewers,
    ...sortedDesignReviewers,
    ...sortedSubmitterUsers
  ];
  const allProjectUsersMap = createUserMap(allProjectUsers);

  return {
    dataLoaded: true,
    gcReviewers: sortedGcReviewers,
    designReviewers: sortedDesignReviewers,
    tradePartners,
    responsibleContractors: sortedResponsibleContractors,
    drCompanies: sortedDrCompanies,
    submitterUsers: sortedSubmitterUsers,
    materialTradePartners: sortedMaterialTradePartners,
    allProjectUsersMap
  };
};

// ============ MAIN HOOK ============
export const useProjectParticipants = (skip?: boolean) => {
  const { projectId } = useParams() as any;
  const { gqlClientForProject, eventLogs }: TProjectContext =
    useContext(ProjectContext);

  const { data: projectUsersResponse, refetch: refetchProjectUsers } =
    useCIQQuery(QUERY_PROJECT_USERS, {
      client: gqlClientForProject,
      variables: {
        where: {}
      },
      skip: skip || !gqlClientForProject
    });

  const { data: projectCompaniesResponse, refetch: refetchProjectCompanies } =
    useCIQQuery(QUERY_PROJECT_VENDORS, {
      client: gqlClientForProject,
      variables: {},
      skip: skip || !gqlClientForProject
    });

  const {
    data: subscriptionCompaniesResponse,
    refetch: refetchSubscriptionCompanies
  } = useCIQQuery(QUERY_COMPANY_LIST, {
    client: gqlClientForProject,
    skip: skip || !gqlClientForProject
  });

  const [allParticipants, setAllParticipants] = useState<ProjectParticipants>(
    INITIAL_PARTICIPANTS_STATE
  );

  // TODO: Incrementally update by identifying changed IDs.
  const handleUpdates = useCallback(
    (logs: TAuditChangeLogs) => {
      const dataSources = [
        "project_vendors",
        "project_users"
        // "user"
      ];
      const projectChangeLog = logs.find((event: TAuditChangeLog) =>
        dataSources.includes(event.data_source)
      );
      if (projectChangeLog && projectChangeLog.project_id === projectId) {
        refetchProjectUsers();
        refetchSubscriptionCompanies();
        refetchProjectCompanies();
      }
    },
    [
      projectId,
      refetchProjectCompanies,
      refetchProjectUsers,
      refetchSubscriptionCompanies
    ]
  );

  useEffect(() => {
    if (!eventLogs || !eventLogs.length) return;
    handleUpdates(eventLogs);
  }, [eventLogs, handleUpdates]);

  useEffect(() => {
    const projectUsers = projectUsersResponse?.project_users;
    const projectCompanies = projectCompaniesResponse?.project_vendors;
    const subscriptionCompanies =
      subscriptionCompaniesResponse?.subscription_vendors;

    if (!projectUsers || !projectCompanies || !subscriptionCompanies) return;

    const processed = processParticipantsData(
      projectUsers,
      projectCompanies,
      subscriptionCompanies
    );
    setAllParticipants(processed);
  }, [
    projectCompaniesResponse,
    subscriptionCompaniesResponse,
    projectUsersResponse
  ]);

  const getSubmittalUserById = (id: string, collection: string) => {
    return allParticipants[collection as keyof ProjectParticipants].find(
      (user: any) => {
        return (user.id || user.user_id) === id;
      }
    );
  };

  const getSubmittalCompanyById = (id: string, collection: string) => {
    return allParticipants[collection as keyof ProjectParticipants].find(
      (comp: any) => {
        return comp.vendor_id === id;
      }
    );
  };

  return {
    projectParticipants: allParticipants,
    getSubmittalUserById,
    getSubmittalCompanyById
  };
};

export const useIntegrationProjectParticipants = (skip?: boolean) => {
  const { projectId } = useParams() as any;
  const { gqlClientForProject, eventLogs }: TProjectContext =
    useContext(ProjectContext);

  const [allParticipants, setAllParticipants] = useState<ProjectParticipants>({
    dataLoaded: false,
    gcReviewers: [],
    designReviewers: [],
    tradePartners: [],
    responsibleContractors: [],
    drCompanies: [],
    submitterUsers: [],
    materialTradePartners: [],
    allProjectUsersMap: null
  });

  const { data: integrationUsersData, refetch: refetchIntegrationUsersData } =
    useCIQQuery(QUERY_PROJECT_INTEGRATION_USERS, {
      client: gqlClientForProject,
      skip: skip || !gqlClientForProject
    });

  const handleUpdates = useCallback(
    (logs: TAuditChangeLogs) => {
      const projectChangeLog = logs.find(
        (event: TAuditChangeLog) => event.data_source === "import_log"
      );
      if (projectChangeLog && projectChangeLog.project_id === projectId) {
        refetchIntegrationUsersData();
      }
    },
    [projectId, refetchIntegrationUsersData]
  );

  useEffect(() => {
    if (!eventLogs || !eventLogs.length) return;
    handleUpdates(eventLogs);
  }, [eventLogs, handleUpdates]);

  useEffect(() => {
    const allUsers: any = [];
    const responsibleContractors: any = [];
    const responsibleContractorIds: any = [];

    // subscription_vendor?.name

    integrationUsersData?.project_integration_users.forEach((user: any) => {
      if (
        user.project_integration_vendor?.id &&
        !responsibleContractorIds.includes(user.project_integration_vendor?.id)
      ) {
        responsibleContractors.push({
          vendor_id: user.project_integration_vendor?.id,
          subscription_vendor: {
            name: user.project_integration_vendor?.name
          },
          project_vendors_company_status: {
            status: ""
          }
        });
        responsibleContractorIds.push(user.project_integration_vendor?.id);
      }

      let statusId;

      if (user?.user) {
        const projArr = user?.user.project_users;
        if (projArr && projArr?.length > 0) {
          statusId = projArr[0]?.status_id;
        }
      }

      allUsers.push({
        id: user.id,
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        company: {
          name: user.project_integration_vendor?.name,
          inviteStatus: null,
          vendor_id: user.project_integration_vendor?.id
        },
        type: "actual",
        status_id: statusId
      });
    });

    const sortedAllUsers = allUsers.sort((a: any, b: any) =>
      a.first_name.localeCompare(b.first_name)
    );

    const tmpAllUsers: any = {
      dataLoaded: !!integrationUsersData,
      gcReviewers: sortedAllUsers,
      designReviewers: sortedAllUsers,
      submitterUsers: sortedAllUsers,
      responsibleContractors: responsibleContractors.sort((a: any, b: any) =>
        a.subscription_vendor?.name?.localeCompare(b.subscription_vendor?.name)
      )
    };

    const allProjectUsersMap: any = {};
    for (let p = 0; p < sortedAllUsers.length; p += 1) {
      const id = sortedAllUsers[p].id || sortedAllUsers[p].user_id;
      allProjectUsersMap[id] = sortedAllUsers[p];
    }
    tmpAllUsers.allProjectUsersMap = allProjectUsersMap;

    setAllParticipants(tmpAllUsers);
  }, [integrationUsersData]);

  return {
    data: allParticipants
  };
};

export const getSubmittalUserById = (id: string, collection: any[]) => {
  return collection.find((user: any) => {
    return (user.id || user.user_id) === id;
  });
};

export const getSubmittalCompanyById = (id: string, collection: any[]) => {
  return collection.find((comp: any) => {
    return comp.vendor_id === id;
  });
};
